import logger from './logs';

interface ExpirableData<T> {
    _expirableValue: T;
    _expiry: number;
}

export const loadFromLocalStorage = <T>(key: string): T | null => {
    const data = localStorage.getItem(key);

    // if the item doesn't exist, return null
    if (!data) {
        return null;
    }

    let parsedData: ExpirableData<T> | T | null;

    try {
        parsedData = JSON.parse(data) as ExpirableData<T> | T | null;
    } catch (error) {
        parsedData = null;
    }

    if (parsedData && (parsedData as ExpirableData<T>)._expirableValue) {
        const expirableData: ExpirableData<T> = parsedData as ExpirableData<T>;
        const now = new Date();

        // compare the expiry time of the item with the current time
        if (now.getTime() > expirableData._expiry) {
            // if item expired, delete the item from storage and return null
            logger.log('utils > loadFromLocalStorage > data expired, deleting key from localStorage', { key });
            clearFromLocalStorage(key);
            return null;
        }

        logger.log('utils > loadFromLocalStorage > retrieving data that has ttl', { key, expirableData });
        return expirableData._expirableValue;
    }

    logger.log('utils > loadFromLocalStorage', { key, data: parsedData });
    return parsedData as T;
};

// ttl: time to live
export const saveToLocalStorage = <T>(key: string, data: T, ttl?: number) => {
    let objectToSave: ExpirableData<T> | T;

    if (ttl) {
        const now = new Date();

        // contains the original value
        // as well as the time when it's supposed to expire
        objectToSave = {
            _expirableValue: data,
            _expiry: now.getTime() + ttl,
        };
        logger.log('utils > saveToLocalStorage', { key, data, objectToSave });
    } else {
        objectToSave = data;
        logger.log('utils > saveToLocalStorage with ttl', { key, data, ttl, objectToSave });
    }

    localStorage.setItem(key, JSON.stringify(objectToSave));
};

export const clearFromLocalStorage = (key: string): void => {
    logger.log('utils > clearFromLocalStorage', { key });

    localStorage.removeItem(key);
};
